/* eslint-disable react/display-name */
import React, { useCallback, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { useMessage } from 'modules/intl';
import { SelectLabelOption } from 'graphql/types';
import FacetDetailField from '../FacetDetailsField';
import { LabelValueType } from '../LabelFacetDetails';
import { AddCustomLabel } from './AddCustomLabel';
import { CustomLabelKeySelector } from './CustomLabelKeySelector';
import { CustomLabelValueSelector } from './CustomLabelValueSelector';

export type CustomLabelCriterion = LabelValueType;

export type CustomLabelTag = {
  id?: string | null;
  name?: string | null;
  values?: SelectLabelOption[] | null;
};

export type CustomLabelFacetDetailsProps = {
  selected: CustomLabelCriterion[];
  setSelected: (values: CustomLabelCriterion[]) => void;
};

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1
  }
}));

export const getDetailsControl = (
  customLabels: CustomLabel[]
): React.FC<CustomLabelFacetDetailsProps> => ({
  selected,
  setSelected
}: CustomLabelFacetDetailsProps) => {
  const classes = useStyles();
  const [scrollToAdd, setScrollToAdd] = useState(false);
  const [labelTags, setLabelTags] = useState<CustomLabelTag[]>(
    selected.map((l) => ({
      id: l.labelId,
      name: l.displayText,
      values: l.values
    }))
  );
  const labelName = useMessage('labelType.customLabel');
  const onAddCustomLabelClick = useCallback(() => {
    setLabelTags((labels) => [...labels, {}]);
  }, []);

  const onCustomLabelValueSelect = useCallback(
    (tag: CustomLabelTag, index: number) => (
      values: SelectLabelOption[] | null
    ) => {
      labelTags[index].values = values;
      if (values && values.length) {
        labelTags[index].values = values;
        selected[index] = {
          displayText: tag.name!,
          label: `${tag.name}`,
          labelId: tag.id!,
          value: tag.id!,
          id: tag.id!,
          key: `${tag.name}+${values.map((v) => v.name).join('+')}`,
          values: values
        };
      } else {
        selected[index] = {
          displayText: tag.name!,
          label: `${tag.name}`,
          labelId: tag.id!,
          value: tag.id!,
          id: tag.id!,
          key: `${tag.name}`
        };
      }
      setSelected([...selected.filter((s) => !!s)]);
    },
    [labelTags, selected, setSelected]
  );

  const onKeyAdded = useCallback(
    (index: number) => (label: CustomLabel | null) => {
      if (label) {
        setLabelTags((labels) => {
          labels[index] = {
            id: label.id,
            name: label.name
          };
          return [...labels];
        });
        setScrollToAdd(true);
      }
    },
    []
  );

  const onKeyRemove = useCallback(
    (index: number) => () => {
      setLabelTags((labels) => {
        labels.splice(index, 1);
        return [...labels];
      });
      selected.splice(index, 1);
      setSelected([...selected]);
    },
    [selected, setSelected]
  );
  const selectedKeys = useMemo<Set<string>>(
    () =>
      labelTags.reduce((acc, label) => {
        if (label.id) {
          acc.add(label.id);
        }
        return acc;
      }, new Set<string>()),
    [labelTags]
  );

  return (
    <FacetDetailField label={labelName}>
      <div className={classes.root}>
        {labelTags.map((label, i) =>
          Object.keys(label).length ? (
            <CustomLabelValueSelector
              customLabel={label}
              onRemove={onKeyRemove(i)}
              key={`facet-${label.id}`}
              onChange={onCustomLabelValueSelect(label, i)}
              values={label.values}
            />
          ) : (
            <CustomLabelKeySelector
              key={`facet-${label.id}`}
              selectedKeys={selectedKeys}
              labels={customLabels}
              onChange={onKeyAdded(i)}
              onRemove={onKeyRemove(i)}
            />
          )
        )}
        {labelTags.length < customLabels.length &&
        labelTags.every((label) => label.id) ? (
          <AddCustomLabel
            onClick={onAddCustomLabelClick}
            autoSelect={scrollToAdd}
          />
        ) : (
          <></>
        )}
      </div>
    </FacetDetailField>
  );
};
